/**
 * Earn Rules Grid Skeleton Pattern
 *
 * Skeleton loaders for the earn rules list block.
 * Shows placeholder cards while data is loading.
 */
import type { FC } from 'react';
import { Skeleton } from '../index';
import { ParagraphSkeleton } from './paragraph';

export interface EarnRulesGridSkeletonProps {
	/**
	 * Number of columns to display
	 * @default 3
	 */
	columns?: number;

	/**
	 * Optional aria message for screen readers
	 */
	ariaMessage?: string;
}

/**
 * Earn rules grid skeleton component
 *
 * Renders a grid of skeleton cards matching the earn rules layout.
 */
export const EarnRulesGridSkeleton: FC<EarnRulesGridSkeletonProps> = ({
	columns = 3,
	ariaMessage = 'Loading earn rules...',
}) => (
	<div
		className="leat-earn-rules-list__grid"
		style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}
		aria-live="polite"
		aria-label={ariaMessage}
	>
		{Array.from({ length: columns }).map((_, i) => (
			<EarnRuleCardSkeleton key={i} />
		))}
	</div>
);

/**
 * Individual earn rule card skeleton
 */
const EarnRuleCardSkeleton: FC = () => (
	<div className="leat-earn-rule-card leat-earn-rule-card--skeleton">
		<div className="leat-earn-rule-card__icon">
			<Skeleton width="64px" height="64px" borderRadius="50%" />
		</div>
		<div className="leat-earn-rule-card__label">
			<ParagraphSkeleton lines={2} />
		</div>
		<div className="leat-earn-rule-card__credits">
			<Skeleton width="80px" height="24px" />
		</div>
		<div className="leat-earn-rule-card__action">
			<Skeleton width="100%" height="36px" borderRadius="4px" />
		</div>
	</div>
);
